/**
 * FS-04 한 판의 상태 — 커널 이벤트(C-05 KernelEvent)와 진행 표본을 받아 HUD·결과를 만든다(순수 · 시험 대상).
 *
 * 상태(SCR v0.6 FS-04-02): 주행 중 · 막힘(빨간 포트·스톨, 리셋·해소 시 해제) · 추락→리스폰 · 타이머 만료.
 * 결과(FS-04-03): 완주/미완주 · 내 시간 · 시도 횟수(본인만 — SR-ACH-01) · 멈춘 곳 · 힌트 재료.
 */
import type { KernelEvent } from "@picnic-field/local-host/protocol";
import type { RunState, RunAction } from "../types";

export const initialRun = (): RunState => ({
  phase: "ready",
  stalls: [],
  attempts: 1,
  respawn: null,
  fe: [],
  stallSurfaces: [],
  fallSurfaces: [],
  analysis: null,
  best: { progress: 0, surface: null },
  now: { progress: 0, surface: null, timeMs: 0 },
  result: null,
});

const addOnce = (xs: string[], x: string | null | undefined) =>
  x == null || xs.includes(x) ? xs : [...xs, x];

export function runReducer(s: RunState, a: RunAction): RunState {
  switch (a.type) {
    case "reset":
      return initialRun();
    case "start":
      return s.phase === "ready" ? { ...s, phase: "driving" } : s;
    case "pause":
      return s.phase === "driving" ? { ...s, phase: "paused" } : s;
    case "resume":
      return s.phase === "paused" ? { ...s, phase: "driving" } : s;
    case "sample": {
      if (s.phase === "over") return s;
      // 발밑이 비면(길 밖 · 떨어지는 중) 마지막으로 밟은 표면을 유지한다 — 다리에서 떨어지면 '다리'
      const surface = a.surface ?? s.now.surface;
      const best = a.progress > s.best.progress ? { progress: a.progress, surface } : s.best;
      // 막힌 동안 밟고 있는 표면(이벤트는 프레임보다 먼저 와서 표본으로 기록한다)
      const stallSurfaces =
        s.stalls.length && a.surface ? addOnce(s.stallSurfaces, a.surface) : s.stallSurfaces;
      return {
        ...s,
        best,
        stallSurfaces,
        now: { progress: a.progress, surface, timeMs: a.timeMs },
      };
    }
    case "kernel":
      return onKernel(s, a.event);
  }
}

function onKernel(s: RunState, e: KernelEvent): RunState {
  if (s.phase === "over") return s;
  switch (e.type) {
    case "stall":
      return { ...s, stalls: addOnce(s.stalls, e.port) };
    case "stall-cleared":
      return { ...s, stalls: s.stalls.filter((p) => p !== e.port) };
    case "respawn":
      return {
        ...s,
        // 리셋·리스폰은 막힘을 푼다(SCR: '리셋·해소 시 해제')
        stalls: [],
        attempts: e.attempt,
        respawn: { reason: e.reason, attempt: e.attempt, seq: (s.respawn?.seq ?? 0) + 1 },
        fallSurfaces:
          e.reason === "fall" ? addOnce(s.fallSurfaces, s.now.surface ?? "ground") : s.fallSurfaces,
        analysis: e.reason === "fall" && e.analysis ? e.analysis : s.analysis,
      };
    case "finish":
      return {
        ...s,
        phase: "over",
        stalls: [],
        attempts: e.attempts,
        result: { completed: true, timeMs: e.timeMs, attempts: e.attempts, reason: "finish" },
      };
    case "fail":
      return {
        ...s,
        phase: "over",
        stalls: [],
        attempts: e.attempts,
        analysis: e.analysis ?? s.analysis,
        result: {
          completed: false,
          timeMs: s.now.timeMs,
          attempts: e.attempts,
          reason: e.reason,
        },
      };
    case "fe":
      return { ...s, fe: addOnce(s.fe, e.code) };
    default:
      return s;
  }
}
